// 動態載入（webpack async chunk）失敗時自動重新整理一次（issue #314）。
//
// 為什麼：部署換版後，開著的頁面（或瀏覽器快取的舊 index.html／app.js）引用的是上一版的 chunk 檔名；
// 舊檔一旦不在了（或 dev 建置同名檔內容已換）就會 ChunkLoadError。重新整理一次拿到新版 index.html 就好了。
// 部署端（scripts/deploy-s3.mjs）已改成舊檔保留 14 天、index.html／config.js 設 no-cache，這裡是第三層防呆。
//
// 規則（純函式 decideChunkReload／markChunkReload，node --test 直接測：tests/chunk-reload.test.mjs）：
//   - 只處理 chunk 載入失敗（isChunkLoadError）；其他錯誤一律不重整。
//   - 列印模式（isPrintMode：雲端 PDF 渲染器以 headless Chromium 擷取）不重整——中途重載會打亂擷取，維持顯示錯誤、走 PDF 失敗路徑。
//   - embedded（後台 ReportViewerDialog、病患紀錄頁直接掛 <ReportPage embedded>）不自動重整——重整的是整個後台頁、狀態會消失；
//     顯示錯誤畫面，使用者按「重試」才整頁重新整理（2026-10-06 裁定）。
//   - 防無限迴圈＝「這一頁本身是不是自動重整出來的」，與失敗發生在載入後多久無關（webpack chunk 逾時 120 秒型的失敗也只重整一次）：
//     要重整的那一刻（markChunkReload）在 sessionStorage 記 { url, at }；之後每一頁啟動時記下 PAGE_STARTED_AT（本模組載入的時間，
//     與 at 同一個時鐘 Date.now）。同網址的旗標時間 ≥ 本頁啟動 − RELOAD_ARRIVAL_MS（＝本頁是那次重整的產物，或本頁已決定要重整）
//     → 不再重整。之後另一次正常載入（啟動時間比旗標晚超過 RELOAD_ARRIVAL_MS）遇到 chunk 錯誤仍可自動重整一次。
//   - sessionStorage 不可用（取用、讀、寫丟例外，或寫了讀不回）＝不自動重整，照舊顯示錯誤；任何情況都不 throw。
//   - 旗標只在「確定要重整」的那一刻寫（呼叫端先上報、確認元件還在、沒換報告，才 mark → reload），不在決定時寫。
// 呼叫端（src/pages/Report/index.vue 的 mountRenderer、src/routes/index.js 的 router.onError、報告改版工單 46 起
// src/report/NextRenderer.vue 的 reloadOnChunkError〔新渲染器的區塊與文案 chunk；列印模式改為原地重試一次、仍失敗整頁錯誤〕）
// 負責上報與畫面；這裡只做「要不要重整」的決定與旗標。
// import 規則：本檔不 import 任何東西（S2 以 tests/helpers/import-src.mjs 載入）。

export const CHUNK_RELOAD_STORAGE_KEY = 'metasens.chunkReload'
const PROBE_KEY = 'metasens.chunkReload.probe'
// 寫下旗標 → 重整後的新頁面啟動（app.js 執行到本模組）之間容許的最長時間。比這更慢才啟動的頁面會被當成「不是重整產物」
// 而可能再重整一次（每輪都要慢過這個時間才會發生，實務上等於頁面打不開）；太長的代價只是：自動重整後這段時間內使用者
// 自己再重新整理同一網址，遇到 chunk 錯誤時不會自動重整（顯示錯誤畫面，可按「重試」）。
export const RELOAD_ARRIVAL_MS = 60 * 1000
// 本頁啟動時間（本模組第一次載入；app.js 由 router 引入，啟動時就執行）。與旗標的 at 同一個時鐘（Date.now）。
export const PAGE_STARTED_AT = Date.now()

// webpack 5 JS chunk：error.name='ChunkLoadError'、message='Loading chunk <id> failed.\n(<type>: <url>)'；
// mini-css-extract-plugin 2.x CSS chunk：message='Loading CSS chunk <id> failed.\n(<url>)'、code='CSS_CHUNK_LOAD_FAILED'。
const CHUNK_MESSAGE = /Loading (CSS )?chunk \S+ failed/i

/** 是否為 chunk 載入失敗（webpack ChunkLoadError／Loading chunk … failed／Loading CSS chunk … failed）。 */
export function isChunkLoadError (error) {
  if (!error || typeof error !== 'object') return false
  if (error.name === 'ChunkLoadError') return true
  if (error.code === 'CSS_CHUNK_LOAD_FAILED') return true
  return CHUNK_MESSAGE.test(String(error.message || ''))
}

function readRecord (storage) {
  const raw = storage.getItem(CHUNK_RELOAD_STORAGE_KEY)
  if (!raw) return null
  try {
    const rec = JSON.parse(raw)
    return rec && typeof rec === 'object' ? rec : null
  } catch (e) {
    return null
  }
}

/**
 * 決定這次 chunk 載入失敗要不要自動重新整理（唯讀：不寫旗標；只用另一個探測鍵確認 sessionStorage 可寫）。不 throw。
 * @param {object} opts
 * @param {*}        opts.error          捕捉到的例外
 * @param {boolean}  [opts.isPrintMode]  列印模式（PDF 擷取）
 * @param {boolean}  [opts.embedded]     報告元件直掛在後台頁裡
 * @param {string}   opts.url            要重整到的網址（通常是 window.location.href；路由層＝目標路由的網址）
 * @param {number}   opts.pageStartedAt  本頁啟動時間（ms，與旗標同一時鐘）
 * @param {function} opts.getStorage     回傳 sessionStorage（取用本身可能丟例外——無痕／封鎖網站資料）
 * @param {number}   [opts.arrivalMs]
 * @returns {{ reload: boolean, reason: 'not-chunk'|'print-mode'|'embedded'|'invalid-input'|'storage-unavailable'|'page-is-auto-reload'|'reload' }}
 */
export function decideChunkReload ({ error, isPrintMode, embedded, url, pageStartedAt, getStorage, arrivalMs = RELOAD_ARRIVAL_MS } = {}) {
  if (!isChunkLoadError(error)) return { reload: false, reason: 'not-chunk' }
  if (isPrintMode) return { reload: false, reason: 'print-mode' }
  if (embedded) return { reload: false, reason: 'embedded' }
  const started = Number(pageStartedAt)
  const target = String(url || '')
  if (!target || !Number.isFinite(started)) return { reload: false, reason: 'invalid-input' }
  try {
    const storage = typeof getStorage === 'function' ? getStorage() : null
    if (!storage) return { reload: false, reason: 'storage-unavailable' }
    const prev = readRecord(storage)
    // 旗標比本頁啟動晚也算（本頁已決定重整、或時鐘往回調）：寧可少重整一次，不可迴圈
    if (prev && prev.url === target && Number(prev.at) >= started - arrivalMs) {
      return { reload: false, reason: 'page-is-auto-reload' }
    }
    // 可寫才重整（重整前要寫得進旗標，否則新頁面無從得知自己是重整產物）
    storage.setItem(PROBE_KEY, '1')
    const ok = storage.getItem(PROBE_KEY) === '1'
    if (typeof storage.removeItem === 'function') storage.removeItem(PROBE_KEY)
    if (!ok) return { reload: false, reason: 'storage-unavailable' }
    return { reload: true, reason: 'reload' }
  } catch (e) {
    return { reload: false, reason: 'storage-unavailable' }
  }
}

/** 確定要重整的那一刻寫旗標 { url, at: now }；寫不進去（或讀不回）回 false＝不要重整。不 throw。 */
export function markChunkReload ({ url, now, getStorage } = {}) {
  const at = Number(now)
  const target = String(url || '')
  if (!target || !Number.isFinite(at)) return false
  try {
    const storage = typeof getStorage === 'function' ? getStorage() : null
    if (!storage) return false
    const value = JSON.stringify({ url: target, at })
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, value)
    return storage.getItem(CHUNK_RELOAD_STORAGE_KEY) === value
  } catch (e) {
    return false
  }
}

const browserStorage = () => window.sessionStorage
const currentUrl = () => (typeof window !== 'undefined' && window.location ? window.location.href : '')

/** 瀏覽器端的 decideChunkReload：本頁啟動時間與 sessionStorage 取自本模組／window。不 throw。 */
export function decideChunkReloadInBrowser ({ error, isPrintMode, embedded, url } = {}) {
  return decideChunkReload({ error, isPrintMode, embedded, url: url || currentUrl(), pageStartedAt: PAGE_STARTED_AT, getStorage: browserStorage })
}

/** 瀏覽器端的 markChunkReload（現在時間＝Date.now()）。 */
export function markChunkReloadInBrowser (url) {
  return markChunkReload({ url: url || currentUrl(), now: Date.now(), getStorage: browserStorage })
}

/** 整頁重新整理到 url：與目前網址相同＝reload；否則（路由層換頁失敗）＝導到目標網址。 */
export function reloadPage (url) {
  if (!url || url === window.location.href) window.location.reload()
  else window.location.assign(url)
}

/**
 * 等 promise 結束（成功或失敗都算）或 ms 毫秒到，取先到者；用在「先上報、再重整」——上報是 fetch，
 * 頁面一卸載就會被取消，所以重整前等它送完，但最多等 ms（網路掛住時不無限等）。
 */
export function settleWithin (promise, ms) {
  return new Promise(resolve => {
    let timer = null
    const finish = () => {
      if (timer === null) return
      clearTimeout(timer)
      timer = null
      resolve()
    }
    timer = setTimeout(finish, ms)
    Promise.resolve(promise).then(finish, finish)
  })
}
